import { useState } from 'react';

import { AppIcon, AppIcons, InfoModal, lottieAnimations, UploadedFile } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { showSnackbar } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { IngestLinkResponse } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  StoryDocument,
  useUpdateUserProfileMutation,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';

import {
  Box,
  Button,
  MenuItem,
  Select,
  Stack,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import Lottie from 'lottie-react';

import { FileUploadConfig } from '../types/profile.types';
import { getUnviewableLinkMessage } from '../utils/linkDocument.util';

const MODAL_DOCUMENT_STRINGS = appStrings.modules.profile.modalUploader.document;
const LINK_STRINGS = MODAL_DOCUMENT_STRINGS.link;
const MAX_CHAR_LIMIT = 50;

// A document being saved is either an uploaded file or an external link. The
// link's ingest runs in the background, so `result` is null until it resolves.
export type DocumentUploadSource =
  | { kind: 'file'; file: File; mediaId: number }
  | { kind: 'link'; url: string; result: IngestLinkResponse | null };

type ProfileDocumentUploadModalProps = Readonly<{
  open: boolean;
  source: DocumentUploadSource;

  modalTitle: string;
  modalDescription: string;
  mediaType: 'document' | 'video';
  fileUploadConfig: FileUploadConfig;

  icon: AppIcons;

  onClose: () => void;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  userId: number;
}>;

const formatFileSize = (bytes: number): string =>
  bytes > 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(2)} MB`
    : `${(bytes / 1024).toFixed(2)} KB`;

export const ProfileDocumentUploadModal = ({
  open,
  source,
  modalTitle,
  modalDescription,
  mediaType,
  fileUploadConfig,
  icon,
  onClose,
  onSuccess,
  onError,
  userId,
}: ProfileDocumentUploadModalProps) => {
  const dispatch = useAppDispatch();
  const [updateUserProfile] = useUpdateUserProfileMutation();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'panel'>(
    fileUploadConfig.visibility ?? 'public',
  );

  const { questionId } = fileUploadConfig;

  const isIngestPending = source.kind === 'link' && !source.result;
  const canChooseVisibility = fileUploadConfig.visibility === 'public';

  const submitLabel = MODAL_DOCUMENT_STRINGS.modal.addDocument;

  const handleSubmit = async () => {
    try {
      if (!userId) throw new Error('Missing user id');

      const getMediaType = (): StoryDocument['media_type'] => {
        if (source.kind === 'link') {
          return source.result?.viewable ? (source.result.media_type ?? 'link') : 'link';
        }

        const fileExtension = source.file.name.split('.').pop() || '';

        if (['mp4', 'mov'].includes(fileExtension)) {
          return 'video';
        }

        if (['png', 'jpg', 'jpeg', 'gif'].includes(fileExtension)) {
          return 'image';
        }

        if (
          ['pdf', 'doc', 'docx', 'txt', 'csv', 'xls', 'xlsx', 'ppt', 'pptx'].includes(fileExtension)
        ) {
          return 'document';
        }

        return mediaType;
      };

      const getSourceFields = (): Pick<
        StoryDocument,
        'media_id' | 'source_url' | 'refresh_policy' | 'viewable'
      > | null => {
        if (source.kind === 'file') {
          return { media_id: source.mediaId };
        }

        const { result, url } = source;

        if (!result) return null;

        return {
          source_url: result.source_url ?? url,
          refresh_policy: result.refresh_policy,
          // viewable is persisted as a string, not a boolean.
          viewable: result.viewable ? 'true' : 'false',
          // media_id only when the link is viewable and produced a stored file.
          ...(result.viewable && result.media_id ? { media_id: result.media_id } : {}),
        };
      };

      const sourceFields = getSourceFields();

      // The submit button is disabled until a link's ingest resolves.
      if (!sourceFields) return;

      const profileData: Omit<StoryDocument, 'id' | 'media_url' | 'profile_option_answers_id'> = {
        user_id: userId,
        category_id: fileUploadConfig.categoryId,
        question_id: questionId || '',
        media_type: getMediaType(),
        title,
        description,
        visibility,
        option_id: fileUploadConfig.optionId as
          | 'resume'
          | 'transcript'
          | 'other'
          | 'personal_assessment'
          | null,
        ...sourceFields,
      };

      await updateUserProfile({
        field: fileUploadConfig.key,
        value: profileData,
      }).unwrap();

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      // The link was already read successfully; only the profile save failed.
      if (source.kind === 'link') {
        dispatch(showSnackbar({ message: LINK_STRINGS.saveError, severity: 'error' }));
      }

      onError?.(error as Error);
    }
  };

  return (
    <InfoModal
      headerContent={
        <Box>
          <Typography variant="text-lg-semibold">{modalTitle}</Typography>
          <Typography variant="text-sm-regular" sx={modalDescriptionSx}>
            {modalDescription}
          </Typography>

          {source.kind === 'link' ? (
            <UploadedFile
              fileName={source.result?.filename || source.url}
              fileSize=""
              fileType="link"
              handleDelete={onClose}
            />
          ) : (
            <UploadedFile
              fileName={
                source.file.name.length > 60
                  ? source.file.name.slice(0, 60) + '...'
                  : source.file.name
              }
              fileSize={formatFileSize(source.file.size)}
              fileType={source.file.name.split('.').pop() || ''}
              handleDelete={onClose}
            />
          )}

          {source.kind === 'link' && source.result && !source.result.viewable && (
            <Box sx={warningRowSx}>
              <Box sx={warningIconSx}>
                <AppIcon
                  iconName="AlertTriangle"
                  diameter="16px"
                  color="Colors/Text/text-warning-primary"
                />
              </Box>
              <Typography
                variant="text-sm-regular"
                color={({ backrs }) => backrs.colors['Colors/Text/text-warning-primary']}
              >
                {getUnviewableLinkMessage(source.result)}
              </Typography>
            </Box>
          )}
        </Box>
      }
      open={open}
      width="sm"
      onClose={onClose}
      icon={icon}
      bodyContent={
        <Stack gap={2}>
          {/**
           * TITLE AND DESCRIPTION
           */}
          <Box>
            <Typography variant="text-sm-medium" sx={formLabelSx}>
              {MODAL_DOCUMENT_STRINGS.modal.title}
            </Typography>
            <TextField
              id="profile-document-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, MAX_CHAR_LIMIT))}
              fullWidth
              helperText={
                title.length >= MAX_CHAR_LIMIT - 10
                  ? `${title.length}/${MAX_CHAR_LIMIT}`
                  : undefined
              }
              inputProps={{ maxLength: MAX_CHAR_LIMIT }}
            />
          </Box>

          <Box>
            <Typography variant="text-sm-medium" sx={formLabelSx}>
              {MODAL_DOCUMENT_STRINGS.modal.description}
            </Typography>
            <TextField
              id="profile-document-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, MAX_CHAR_LIMIT))}
              multiline
              rows={2}
              fullWidth
              helperText={
                description.length >= MAX_CHAR_LIMIT - 10
                  ? `${description.length}/${MAX_CHAR_LIMIT}`
                  : undefined
              }
              inputProps={{ maxLength: MAX_CHAR_LIMIT }}
            />
          </Box>

          {/**
           * VISIBILITY
           */}
          {canChooseVisibility && (
            <Box>
              <Typography variant="text-sm-medium" sx={formLabelSx}>
                {MODAL_DOCUMENT_STRINGS.modal.visibility}
              </Typography>
              <Select
                id="profile-document-visibility-select"
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as 'public' | 'panel')}
                sx={selectSx}
                endAdornment={
                  <Box sx={chevronIconSx}>
                    <AppIcon iconName="ChevronDown" diameter="16px" color="Gray/600" />
                  </Box>
                }
              >
                <MenuItem value="public">{MODAL_DOCUMENT_STRINGS.modal.public}</MenuItem>
                <MenuItem value="panel">{MODAL_DOCUMENT_STRINGS.modal.panel}</MenuItem>
              </Select>
            </Box>
          )}

          <Box sx={submitButtonContainerSx}>
            <Button
              id="profile-document-submit-button"
              variant="contained"
              onClick={handleSubmit}
              hierarchy="Primary"
              size="lg"
              fullWidth
              disabled={!title || !description || !questionId || isIngestPending}
            >
              {isIngestPending ? (
                <Lottie
                  animationData={lottieAnimations.dotsDotting}
                  loop
                  style={submitLoaderStyle}
                />
              ) : (
                submitLabel
              )}
            </Button>
          </Box>
        </Stack>
      }
    />
  );
};

const formLabelSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-xxs'],
  color: backrs.colors['Colors/Text/text-secondary'],
});

const selectSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-primary'],
  width: 'fit-content',
  minWidth: '33%',
});

const modalDescriptionSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-xl'],
});

const warningRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-sm'],
  alignItems: 'flex-start',
  mt: backrs.spacing['spacing-md'],
});

// Center the icon on the text's first line rather than the whole block.
const warningIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  mt: backrs.spacing['spacing-xxs'],
});

const chevronIconSx: SxProps<Theme> = ({ backrs }) => ({
  mr: backrs.spacing['spacing-md'],
});

const submitButtonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  mt: backrs.spacing['spacing-4xl'],
});

const submitLoaderStyle: React.CSSProperties = {
  width: '40px',
  height: '40px',
  margin: '-8px 0',
};
